import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Card, useEffectOnce } from '@chilecompra/react-kit'
import { Button, CheckBox, Toast, Typography, useCheckBox, useViewport } from '@chilecompra/react-kit/components'
import {
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED,
  REACT_APP_TERMS_CONDITIONS_URL,
  REACT_APP_UNIQUE_PASS_SEED
} from '../../../config/settings/environment'
import { onRecoverAdminThunk, onResetRegisterWizardState } from '../RegisterWizard.actions'
import {
  ButtonClave,
  Conditions,
  Hidden,
  Image,
  ImageContainer,
  ImageSuccess,
  Instructions,
  RightSection,
  Root,
  Section,
  Strong,
  TermsLink,
  Title
} from './RegisterWizardRecoveryCompany.styles'
import { onSignInTaxesKeyThunk } from '../../TaxesKeyAuth/TaxesKeyAuth.actions'
import { useCsrfToken } from '../../../modules/hooks/security'
import { useRecaptcha } from '../../../modules/hooks/recaptcha'

/**
 * The RegisterWizardRecoveryCompany' container.
 */
const RegisterWizardRecoveryCompany = props => {
  const { business, errors, loading, isRecoveryAdmin } = useSelector(state => state.registerWizard)
  const dispatch = useDispatch()
  const { size } = useViewport()
  const { onChangeStep } = props
  const [isRecoverInstitutionMode, setIsRecoverInstitutionMode] = useState(true)
  const [isRecoverSuccesful, setIsRecoverSuccesful] = useState(false)
  const { value: checked, onChange: handleChangeCheckbox } = useCheckBox()
  const csrfToken = useCsrfToken(REACT_APP_UNIQUE_PASS_SEED)
  const { executeRecaptcha } = useRecaptcha()

  useEffectOnce(async () => {
    if (isRecoveryAdmin) {
      handleSubmit()
    }
  })

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleSubmit = () => {
    dispatch(
      onRecoverAdminThunk({
        executeRecaptcha,
        onSuccessCallback: () => {
          setIsRecoverInstitutionMode(false)
          setIsRecoverSuccesful(true)
        }
      })
    )
  }

  const handleTaxesKey = () => {
    if (
      isLocalEnv ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === false ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === 'false'
    ) {
      handleSubmit()
    } else {
      dispatch(onSignInTaxesKeyThunk({ csrfToken, dni: business?.dni, isRecoveryAdmin: true }))
    }
  }

  return (
    <Root>
      <Section display="flex" flexDirection={size.isTiny ? 'column' : 'row'} justifyContent="space-between" gap="30px">
        <Section>
          <Section item>
            <Title
              tabIndex="0"
              aria-label={`Paso 3: ${
                isRecoverInstitutionMode
                  ? 'Recuperar institución registrada en Mercado Público'
                  : '¡Recuperación de administración realizada!'
              }`}
            >
              {isRecoverInstitutionMode
                ? 'Recuperar institución registrada en Mercado Público'
                : '¡Recuperación de administración realizada!'}
            </Title>
          </Section>
          {isRecoverInstitutionMode && (
            <Section item margin={size.isTiny ? '20px 0' : '24px 0 32px'} width={size.isUpToLarge ? '472px' : '100%'}>
              <Instructions tabIndex="0">
                Para recuperar la administración de tu institución, te pedimos que leas y aceptes los siguientes{' '}
                <TermsLink
                  tabIndex="0"
                  aria-label="términos y condiciones"
                  href={REACT_APP_TERMS_CONDITIONS_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  términos y condiciones
                </TermsLink>
                . Luego, haz clic en el botón de Clave Tributaria para ingresar con las credenciales de la institución
              </Instructions>
              <Section display="flex" alignItems="center" margin="10px 0">
                <Conditions>
                  <CheckBox
                    tabIndex="0"
                    aria-label="Acepto términos y condiciones"
                    checked={checked}
                    onChange={handleChangeCheckbox}
                    color="primary"
                  />
                  Acepto los&nbsp;
                  <TermsLink
                    tabIndex="0"
                    aria-label="términos y condiciones de uso"
                    href={REACT_APP_TERMS_CONDITIONS_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    términos y condiciones de uso
                  </TermsLink>
                </Conditions>
              </Section>
              <Section
                item
                display="flex"
                flexDirection={size.isDownToLarge ? 'column-reverse' : 'row'}
                alignItems="center"
                justifyContent="space-between"
                margin="25px 0"
                gap="15px"
              >
                <Button
                  tabIndex="0"
                  aria-label="Cancelar"
                  onClick={handleBack}
                  type="button"
                  width={size.isDownToLarge ? '98%' : '156px'}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <ButtonClave
                  tabIndex="0"
                  aria-label="Ingresar a Clave Tributaria"
                  width={size.isDownToLarge ? '98%' : ''}
                  size="medium"
                  alt="Clave Tributaria"
                  onClick={handleTaxesKey}
                  disabled={!checked}
                  loading={loading}
                >
                  Ingresar Clave Tributaria
                </ButtonClave>
              </Section>
            </Section>
          )}
          {isRecoverSuccesful && (
            <Section display="flex" justifyContent="center" flexDirection="column" alignItems="center">
              <ImageContainer>
                <ImageSuccess src="/assets/img/steps/icono_send_sol.svg" alt="¡Solicitud de inscripción enviada!" />
              </ImageContainer>
              <Card
                display="flex"
                flexDirection="column"
                justifyContent="center"
                enableBorder
                color="blank"
                margin="0 0 30px 0"
                padding="20px 20px 10px"
              >
                <Typography tabIndex="0" variant="body1" textAlign="center" margin="0 0 30px">
                  Se te ha asignado el rol de administrador para <br />
                  <Strong>{business.name}</Strong>
                </Typography>

                <Typography tabIndex="0" variant="body1" margin="0 0 20px" textAlign="center">
                  Ya puedes ingresar a Mercado Público con tu Clave Única
                </Typography>
              </Card>
              <Button
                tabIndex="0"
                aria-label="Volver a Mercado Público"
                onClick={handleBack}
                type="button"
                width={size.isTiny ? '95%' : '256px'}
                color="primary"
                margin={size.isTiny ? '30px 0 40px 0' : size.isDownToLarge ? '27px 0 40px 0' : '30px 0'}
              >
                Volver a Mercado Público
              </Button>
            </Section>
          )}
        </Section>

        {errors.recoveryError === 'YOU_ARE_ALREADY_ADMIN_ERROR' && !loading && (
          <Toast tabIndex="0" severity="info">
            Hemos detectado que ya eres administrador de la empresa con RUT {business.dni} en Mercado Público. Puedes
            volver atrás para ingresar un RUT distinto o iniciar sesión en esta empresa
          </Toast>
        )}

        {errors.recoveryError === 'SII_ERROR' && !loading && (
          <Toast severity="warning">
            En estos momentos no podemos acceder a la información debido a un problema de conexión con el Servicio de
            Impuestos Internos, puedes intentar nuevamente en unos minutos para continuar con tu inscripción..
          </Toast>
        )}

        {size.isUpToLarge && !isRecoverSuccesful && (
          <RightSection>
            <Hidden lgDown>
              <Image src="/assets/img/steps/paso4.svg" alt="Inscribir una nueva institución en Mercado Público" />
            </Hidden>
          </RightSection>
        )}
      </Section>
    </Root>
  )
}

RegisterWizardRecoveryCompany.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardRecoveryCompany.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardRecoveryCompany
